import {CheckReservationStatusRequest, CheckReservationStatusResponse, space} from "../../compiled";
import React, {ReactNode, useEffect, useState} from "react";
import {useRpcSender} from "../../RpcSender";
import {useSearchParams} from "react-router-dom";
import {BookingFlowPage} from "./BookingFlowPage";
import {App2} from "../App2";
import {RpcState} from "../../util/RpcState";
import IApiBookingProto = space.kenko.proto.IApiBookingProto;
import {shortDateTimeFormat2} from "../../util/DateTimeUtil";

/**
 * This shows a confirmation for a booking (either class or appointment).
 * It assumes the booking has already been made.
 */
export const Confirmation = () => {

  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('b');

  const [booking, setBooking] = useState<IApiBookingProto | null>();
  const [rpcState, setRpcState] = useState<RpcState>({state: 'pending'});
  const rpcSender = useRpcSender();

  // Kick this off once at the beginning. It will re-kick itself if necessary.
  useEffect(() => {

    const request = CheckReservationStatusRequest.encode(new CheckReservationStatusRequest({
      bookingId: bookingId,
    })).finish();

    rpcSender.send('check_reservation_status', request)
        .then(r => {
          const response = CheckReservationStatusResponse.decode(r);
          console.log('response', response);
          setRpcState({state: 'success'});
          setBooking(response.booking);
        })
        .catch(e => {
          console.warn('Network error checking confirmation status', e);
          setRpcState({state: 'error', message: 'Error checking confirmation status.'});
        });
  }, []);

  const getContent = (): ReactNode => {

    if (rpcState.state == 'pending') {
      return <>Loading..</>
    }

    if (rpcState.state == 'error') {
      return <div className={'ErrorText'}>{rpcState.message}</div>
    }

    if (!booking) {
      return <div className={'ErrorText'}>Couldn't find this booking</div>
    }

    const appointmentLength = Math.round((booking?.endTimeMs as number - (booking?.startTimeMs as number)) / 1000 / 60);
    const serviceName = booking?.details?.service?.serviceName || booking?.details?.class?.className;
    const location = booking?.details?.service?.locationName || booking?.details?.class?.locationName;
    const address = (booking.details?.service?.address || booking.details?.class?.address);
    const virtualMeetingLink = booking.details?.service?.virtualMeetingLink;

    return <div className={'ConfirmationBottomSection'}>
      <div className={'ConfirmationRow'}>
        <h3>
        Your booking is complete!
        </h3>
      </div>

      <div className={'ConfirmationRow'}>
        <div className={'ConfirmationRowLeftIcon'}>
          <img className={'ConfirmationRowLeftImage DarkModeInvert'} src={require('./Images/icon_event.png')}/>
        </div>
        <div className={'ConfirmationRowRight'}>
          <div><b>{shortDateTimeFormat2(booking.startTimeMs)}</b></div>
          <div>{serviceName} ({appointmentLength} min)</div>
        </div>
      </div>
      <div className={'ConfirmationRow'}>
        <div className={'ConfirmationRowLeftIcon'}>
          <img className={'ConfirmationRowLeftImage DarkModeInvert'} src={require('./Images/icon_location.png')}/>
        </div>
        <div className={'ConfirmationRowRight'}>
          {location && <>
          <div><b>{location}</b></div>
          <div>{address}</div>
          </>}
          {booking.details?.service?.isVirtual && <>
            <div><b>Virtual meeting</b></div>
            {virtualMeetingLink && <div>Link:
              <a style={{textDecoration: 'underline'}} target={"_blank"} href={virtualMeetingLink}>{virtualMeetingLink}</a>
            </div>}
            {!virtualMeetingLink && <div>
              Your practitioner will send a virtual meeting link.
            </div>}
          </>}
        </div>
      </div>
      <div className={'ConfirmationRow'}>
        <div className={'ConfirmationRowLeftIcon'}>
          <img className={'ConfirmationRowLeftImage DarkModeInvert'} src={require('./Images/icon_schedule.png')}/>
        </div>
        <div className={'ConfirmationRowRight'}>
          <div>{appointmentLength} minutes</div>
        </div>
      </div>
    </div>;
  }

  return <App2><BookingFlowPage title={'Confirmation'} content={getContent()}/></App2>
}
